// DemoDrawBackground.tsx
import React, { useEffect, useRef, useState } from 'react';

// Fixed demo path (normalized, relative to viewport)
const demoDrawPath = [
  { x: 0.2, y: 0.7, time: 0 },
  { x: 0.3, y: 0.5, time: 600 },
  { x: 0.5, y: 0.3, time: 1200 },
  { x: 0.7, y: 0.5, time: 1800 },
  { x: 0.8, y: 0.7, time: 2400 },
];

const DemoDrawBackground: React.FC = () => {
  const [hand, setHand] = useState({ x: 0, y: 0 });
  const requestRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const animate = () => {
      const now = Date.now();
      const elapsed = ((now - startTimeRef.current) % demoDrawPath[demoDrawPath.length - 1].time);
      let idx = 0;
      while (idx < demoDrawPath.length - 1 && demoDrawPath[idx + 1].time <= elapsed) idx++;
      const p1 = demoDrawPath[idx];
      const p2 = demoDrawPath[idx + 1] || demoDrawPath[0];
      const segTime = p2.time - p1.time;
      const t = segTime ? (elapsed - p1.time) / segTime : 0;
      const x = p1.x + (p2.x - p1.x) * t;
      const y = p1.y + (p2.y - p1.y) * t;
      setHand({ x, y });
      requestRef.current = requestAnimationFrame(animate);
    };
    startTimeRef.current = Date.now();
    requestRef.current = requestAnimationFrame(animate);
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Draw the demo path as the hand moves
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Clear
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Responsive size
    canvas.width = size.width;
    canvas.height = size.height;
    // Find current hand position index
    const now = Date.now();
    const elapsed = ((now - startTimeRef.current) % demoDrawPath[demoDrawPath.length - 1].time);
    let idx = 0;
    while (idx < demoDrawPath.length - 1 && demoDrawPath[idx + 1].time <= elapsed) idx++;
    // Draw path up to hand
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    for (let i = 0; i <= idx; i++) {
      const px = demoDrawPath[i].x * size.width;
      const py = demoDrawPath[i].y * size.height;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    // Draw partial segment to hand
    if (idx < demoDrawPath.length - 1) {
      const p1 = demoDrawPath[idx];
      const p2 = demoDrawPath[idx + 1];
      const segTime = p2.time - p1.time;
      const t = segTime ? (elapsed - p1.time) / segTime : 0;
      const hx = (p1.x + (p2.x - p1.x) * t) * size.width;
      const hy = (p1.y + (p2.y - p1.y) * t) * size.height;
      ctx.lineTo(hx, hy);
    }
    ctx.stroke();
  }, [hand, size]);

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          pointerEvents: 'none',
          zIndex: 10,
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: hand.x * size.width+10,
          top: hand.y * size.height+20,
          transform: 'translate(-50%, -50%) scale(1.3)',
          fontSize: '2.5rem',
          pointerEvents: 'none',
          filter: 'drop-shadow(0 0 8px #fff)',
          zIndex: 11
        }}
      >
        <span role="img" aria-label="hand">👆</span>
      </div>
    </>
  );
};

export default DemoDrawBackground;